import * as i0 from '@angular/core';
import { Injectable, inject, InjectionToken, NgModule } from '@angular/core';
import { HttpResponse, HttpClient, HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { map, mergeMap, reduce, shareReplay, catchError, toArray, ignoreElements } from 'rxjs/operators';
import { Observable, of, merge } from 'rxjs';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars  */
class DaimyoUtilsService {
    isSuccessfulStatusCode(statusCode) {
        return Math.floor(statusCode / 100) === 2;
    }
    filterZonesThatInclude(needle, zones) {
        return zones.filter(zone => !zone.includes(needle));
    }
    ignorePOAZone(zones) {
        return this.filterZonesThatInclude('poa', zones);
    }
    ignoreUnwantedZones({ data }) {
        const zones = Object.keys(data);
        return this.ignorePOAZone(zones);
    }
    parseToArray(data) {
        const wantedZones = Object.keys(data);
        return wantedZones.map(zone => ({ ...data[zone], zone }));
    }
    hasSuccessfulZoneResponse(response) {
        return this.parseToArray(response).some(({ httpCode }) => this.isSuccessfulStatusCode(httpCode));
    }
    hasErrorInResponse(response) {
        return this.parseToArray(response).some(({ httpCode }) => !this.isSuccessfulStatusCode(httpCode));
    }
    findErrorResponses(response) {
        return this.parseToArray(response).filter(({ httpCode }) => !this.isSuccessfulStatusCode(httpCode));
    }
    findSuccessResponses(response) {
        return this.parseToArray(response).filter(({ httpCode }) => this.isSuccessfulStatusCode(httpCode));
    }
    firstData(response) {
        if (this.hasSuccessfulZoneResponse(response)) {
            const result = Object.keys(response)
                .map(key => response[key])
                .find(({ data, httpCode }) => this.isSuccessfulStatusCode(httpCode));
            return (result && result.data) || {};
        }
        throw new Error('No successful zone response returned.');
    }
    wrapByZone(response) {
        return this.findSuccessResponses(response).reduce((total, { zone, data }) => {
            total[zone] = data;
            return total;
        }, {});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoUtilsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoUtilsService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoUtilsService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class FirstDataInterceptor {
    constructor() {
        this.daimyoUtilsService = inject(DaimyoUtilsService);
    }
    intercept(req, next) {
        return next.handle(req).pipe(map(res => {
            const isSingleZoneRequest = req.headers.has('x-daimyo-zone') && req.headers.get('x-daimyo-zone') !== '*';
            const isAllZoneRequest = req.headers.has('x-disable-firstdata') && req.headers.get('x-disable-firstdata') === 'true';
            const isNoWrap = req.headers.has('x-daimyo-nowrap');
            if (res instanceof HttpResponse && isSingleZoneRequest && !isAllZoneRequest && !isNoWrap) {
                const body = this.daimyoUtilsService.firstData(res.body);
                return res.clone({ body });
            }
            return res;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: FirstDataInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: FirstDataInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: FirstDataInterceptor, decorators: [{
            type: Injectable
        }] });

const APP_NAME = new InjectionToken('APP_NAME');

/* eslint-disable @typescript-eslint/no-explicit-any */
const mergeResponses = (additionalRequests$) => (source) => new Observable(observer => {
    source.subscribe({
        next: response => {
            // we don't care about anything besides HttpResponses.
            if (!(response instanceof HttpResponse)) {
                observer.next(response);
                return;
            }
            additionalRequests$
                .pipe(mergeMap(r => r), reduce((acc, val) => ({ ...acc, ...val }), response.body), map(body => response.clone({ body })))
                .subscribe({
                next: mergedResult => {
                    observer.next(mergedResult);
                    observer.complete();
                },
                error: error => observer.error(error),
            });
        },
        error: error => observer.error(error),
    });
});

/* eslint-disable @typescript-eslint/no-explicit-any */
class DaimyoAllZonesInterceptor {
    constructor() {
        this.httpClient = inject(HttpClient);
        this.zones$ = this.httpClient.get('/zones').pipe(shareReplay(1));
    }
    httpResponseToZoneResponse(response, zone) {
        const zoneResponse = response.body;
        return {
            [zone]: {
                data: zoneResponse,
                httpCode: response.status,
                zone,
            },
        };
    }
    httpErrorResponseToZoneResponse(error, zone) {
        return {
            [zone]: {
                httpCode: error.status,
                errors: [{ description: error.message }],
                error: error.statusText,
                zone,
            },
        };
    }
    createDaimyoZoneHttpRequest(originalRequest, zone) {
        return originalRequest.clone({
            setHeaders: {
                'x-daimyo-zone': zone,
                'x-daimyo-nowrap': 'true',
                'x-disable-firstdata': 'true',
            },
        });
    }
    /**
     * Takes a HttpRequest that was created and creates a HttpClient request with the HttpRequest context.
     *
     * @param request
     */
    createRequest(request) {
        return this.httpClient.request(request.method, request.url, {
            body: request.body,
            headers: request.headers,
            params: request.params,
            reportProgress: request.reportProgress,
            responseType: 'json',
            withCredentials: request.withCredentials,
            observe: 'response',
        });
    }
    /**
     * Creates a http request and transforms the data to to emulate Daimyo's response. If the http requests fail for any
     * reason, it will still zone wrap but with an error response instead.
     *
     * @param request
     * @param zone
     */
    createSubRequestForZone(request, zone) {
        return this.createRequest(request).pipe(map(response => this.httpResponseToZoneResponse(response, zone)), catchError((error) => {
            return of(this.httpErrorResponseToZoneResponse(error, zone));
        }));
    }
    /**
     * Creates an array of requests that mutate the original request by calling createDaimyoZoneHttpRequest() for each
     * zone element. If x-daimyo-exclude-zone header is present, the request will not be created for that zone.
     *
     * @see DaimyoAllZonesInterceptor.createDaimyoZoneHttpRequest
     *
     * @param req The original http request to mutate
     * @param zones$ An array of zones to create requests for
     */
    createRequestsForEachZone(req, zones$) {
        const excludedZones = req.headers.getAll('x-daimyo-exclude-zone') || [];
        const zoneBlacklist = excludedZones.reduce((acc, zone) => [...acc, ...zone.split(',')], []);
        return zones$.pipe(map(zones => zones
            .filter(zone => !zoneBlacklist.includes(zone))
            .map(zone => ({ zone, request: this.createDaimyoZoneHttpRequest(req, zone) }))));
    }
    /**
     * Takes in the array of requests, grabs the first request element and passes it to the HttpHandler to continue the
     * original intercepted Http Request. If the http requests fail for any reason, it will still zone wrap but with an
     * error response instead.
     *
     * @param requests$
     * @param next
     */
    createInterceptorRequest(requests$, next) {
        return requests$.pipe(map(([request]) => request), mergeMap(({ request, zone }) => next.handle(request).pipe(map(response => {
            if (response instanceof HttpResponse) {
                return response.clone({ body: this.httpResponseToZoneResponse(response, zone) });
            }
            return response;
        }), catchError((error) => {
            return of(new HttpResponse({
                status: error.status,
                statusText: error.statusText,
                body: this.httpErrorResponseToZoneResponse(error, zone),
            }));
        }))));
    }
    /**
     * Takes in the array of requests, skips the first element and creates requests for the remaining elements.
     *
     * @param requests$
     */
    createAdditionalRequests(requests$) {
        return requests$.pipe(map(([, ...additionalRequests]) => additionalRequests), mergeMap(r => r), mergeMap(({ zone, request }) => this.createSubRequestForZone(request, zone)), toArray());
    }
    intercept(req, next) {
        const isMultiZoneRequest = req.headers.has('x-daimyo-zone') && req.headers.get('x-daimyo-zone') === '*';
        if (!isMultiZoneRequest) {
            return next.handle(req);
        }
        const requests$ = this.createRequestsForEachZone(req, this.zones$).pipe(shareReplay(1));
        // grab the first zone and pass it to the next handler to use this request.
        const firstRequest$ = this.createInterceptorRequest(requests$, next);
        // we skip the first zone because that zone will be handled by this particular instance of the http interceptor
        const additionalRequests$ = this.createAdditionalRequests(requests$).pipe(shareReplay(1));
        // we call merge, but ignore any emitted values so we don't mess with the original request.
        // This allows all requests to fire in a non-blocking way. Without using merge, the original request would
        // have to fire first and then the additional requests would fire in parallel. This is not very good for
        // requests that are not quick, making everything slower on the frontend. The share() operator will
        // cache the response which will be used in the mergeResponses operator. If you're wondering why we can't just
        // use forkJoin, it's due to the fact that we need to return type HttpEVent which is not possible in making a
        // new http request. Since this is an "interceptor" we must have at least one request that goes through it.
        return merge(additionalRequests$.pipe(ignoreElements()), firstRequest$).pipe(mergeResponses(additionalRequests$));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoAllZonesInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoAllZonesInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoAllZonesInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class DaimyoEmulatorInterceptor {
    /**
     * Takes an HttpRequest object, and determines if it is a Single, Nonlocal, Wrapped, Ambassador Request
     *
     * @param req
     */
    isAmbassadorNonLocalSingleReq(req) {
        const isApiGatewayRequest = req.url.indexOf('/api/') === 0;
        const isSingleZoneNonLocalRequest = req.headers.has('x-daimyo-zone') && req.headers.get('x-daimyo-zone') !== '*' && req.headers.get('x-daimyo-zone') !== 'local';
        const isNoWrap = req.headers.has('x-daimyo-nowrap') && req.headers.get('x-daimyo-nowrap') === 'true';
        return isApiGatewayRequest && isSingleZoneNonLocalRequest && !isNoWrap;
    }
    /**
     * Given an HttpResponse object, and zone, generates a zone wrapped response body object
     *
     * @param res
     * @param zone
     */
    generateZoneWrappedResponseBody(res, zone) {
        return {
            body: {
                [zone]: {
                    httpCode: res.status,
                    zone,
                    data: res.body,
                },
            },
        };
    }
    /**
     * Interceptor that will zone wrap an Ambassador API-Gateway response that is single and non-local
     *
     * @param req
     * @param next
     */
    intercept(req, next) {
        if (!this.isAmbassadorNonLocalSingleReq(req)) {
            return next.handle(req);
        }
        const zone = req.headers.get('x-daimyo-zone') || '*';
        return next.handle(req).pipe(map(res => {
            if (res instanceof HttpResponse) {
                return res.clone(this.generateZoneWrappedResponseBody(res, zone));
            }
            return res;
        }), catchError((error) => {
            return of(new HttpResponse({
                status: error.status,
                statusText: error.statusText,
                body: {
                    [zone]: {
                        httpCode: error.status,
                        errors: [{ description: error.message }],
                        error: error.statusText,
                        zone,
                    },
                },
            }));
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoEmulatorInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoEmulatorInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: DaimyoEmulatorInterceptor, decorators: [{
            type: Injectable
        }] });

function configureHttpRootProviders(appName) {
    return [
        DaimyoUtilsService,
        {
            provide: APP_NAME,
            useValue: appName,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FirstDataInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DaimyoAllZonesInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DaimyoEmulatorInterceptor,
            multi: true,
        },
    ];
}
class AtlasHttpClientTestingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientTestingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientTestingModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientTestingModule, providers: [DaimyoUtilsService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientTestingModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [DaimyoUtilsService],
                }]
        }] });
class AtlasHttpClientModule {
    static forRoot({ appName = 'none' } = {}) {
        return {
            ngModule: AtlasHttpClientModule,
            providers: [...configureHttpRootProviders(appName)],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientModule, imports: [HttpClientModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientModule, imports: [HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AtlasHttpClientModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { APP_NAME, AtlasHttpClientModule, AtlasHttpClientTestingModule, DaimyoAllZonesInterceptor, DaimyoEmulatorInterceptor, DaimyoUtilsService, FirstDataInterceptor, configureHttpRootProviders };
